import React, { useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  isSameDay,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import ChevronLeft from 'svg/ChevronLeft';
import ChevronRight from 'svg/ChevronRight';
import Button from 'components/Button';
import classNames from 'styles/utils/classNames';

export interface DatePickerProps {
  /**
   * The currently selected date
   */
  selectedDate: Date | null;

  /**
   * Callback when a date is selected
   */
  onDateChange: (date: Date) => void;

  /**
   * Optional classname for the container
   */
  className?: string;

  /**
   * Optional minimum selectable date (defaults to today)
   */
  minDate?: Date;

  /**
   * Optional maximum selectable date
   */
  maxDate?: Date;

  /**
   * Custom function to determine if a day is selectable
   * If provided, this will be used in addition to the min/max date constraints
   */
  isDaySelectable?: (day: Date) => boolean;

  /**
   * Custom click handler for days
   * If provided, this will be used instead of the default onDateChange handler
   * Can be used for special behaviors like external redirects
   */
  onDayClick?: (day: Date) => void;
}

const DatePicker: React.FC<DatePickerProps> = ({
  selectedDate,
  onDateChange,
  className,
  minDate: customMinDate,
  maxDate,
  isDaySelectable: customIsDaySelectable,
  onDayClick,
}) => {
  // Use selected date as default current month view, or today's date if none selected
  const [currentDate, setCurrentDate] = useState(selectedDate || new Date());

  // Default minimum date is today with time set to start of day
  const minDate =
    customMinDate ||
    (() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    })();

  // Calculate calendar days to display
  const startOfCurrentMonth = startOfMonth(currentDate);
  const endOfCurrentMonth = endOfMonth(currentDate);
  const startOfWeekForCurrentMonth = startOfWeek(startOfCurrentMonth);
  const endOfWeekForCurrentMonth = endOfWeek(endOfCurrentMonth);

  const days = eachDayOfInterval({
    start: startOfWeekForCurrentMonth,
    end: endOfWeekForCurrentMonth,
  });

  // Month navigation handlers
  const handlePrevMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  // Check if a day is selectable based on min/max constraints and custom logic
  const isDaySelectable = (day: Date) => {
    // First check min/max date constraints
    const compareDay = new Date(day);
    compareDay.setHours(0, 0, 0, 0);

    if (minDate && compareDay < minDate) {
      return false;
    }

    if (maxDate) {
      const maxDateCopy = new Date(maxDate);
      maxDateCopy.setHours(23, 59, 59, 999);
      if (compareDay > maxDateCopy) {
        return false;
      }
    }

    // If custom selection logic is provided, apply it as well
    if (customIsDaySelectable) {
      return customIsDaySelectable(day);
    }

    // If no custom constraints, return true
    return true;
  };

  // Handle date selection
  const handleDateSelect = (day: Date) => {
    if (!isDaySelectable(day)) {
      return;
    }

    // If custom click handler is provided, use it
    if (onDayClick) {
      onDayClick(day);
      return;
    }

    // Otherwise call the normal date change handler
    onDateChange(day);
  };

  return (
    <div className={classNames('w-full rounded-3xl', className)}>
      {/* Month navigation */}
      <div className="mb-5 flex w-full items-center justify-between px-4">
        <Button
          variant="inverted"
          className="border-none"
          isInline
          size="icon"
          iconLeft={<ChevronLeft className="h-6 w-6" />}
          onClick={handlePrevMonth}
        />
        <span className="typography-product-subheading mx-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          {format(currentDate, 'MMMM yyyy')}
        </span>
        <Button
          variant="inverted"
          className="border-none"
          isInline
          size="icon"
          iconLeft={<ChevronRight className="h-6 w-6" />}
          onClick={handleNextMonth}
        />
      </div>

      {/* Weekday headers */}
      <div className="grid w-full grid-cols-7 gap-1">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
          <div
            key={day}
            className="py-2 text-center text-sm font-medium text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
          >
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {days.map((day) => {
          const isSelectable = isDaySelectable(day);
          const isCurrentMonth = format(day, 'MM') === format(currentDate, 'MM');

          return (
            <div
              key={day.toString()}
              className={`relative col-auto
                        ${getDay(day) === 0 ? 'col-start-1' : ''}
                        before:block
                        before:pb-[100%]
                        before:content-['']`}
            >
              <button
                type="button"
                onClick={() => handleDateSelect(day)}
                disabled={!isSelectable}
                className={classNames(
                  `absolute bottom-0 left-0 right-0 top-0 flex w-full items-center justify-center
                  rounded-full text-sm`,
                  !isCurrentMonth
                    ? 'text-color-text-lightmode-disabled dark:text-color-text-darkmode-disabled'
                    : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
                  selectedDate !== null && isSameDay(day, selectedDate)
                    ? '!bg-color-bg-lightmode-brand !text-color-text-lightmode-invert !ring-0 dark:!text-color-text-darkmode-invert'
                    : '',
                  isToday(day) ? 'ring-1 ring-black dark:ring-white' : '',
                  !isSelectable
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:bg-color-bg-lightmode-secondary dark:hover:bg-color-bg-darkmode-secondary',
                )}
              >
                {format(day, 'd')}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DatePicker;
